/**
 * Types and utilities for GitHub releases
 * Note: Release data is fetched via backend API, not directly from GitHub
 */

export interface WhatsNewRelease {
  id: number;
  version: string;
  title: string;
  description: string;
  publishedAt: Date;
  url: string;
  isPrerelease: boolean;
  author: {
    name: string;
    avatarUrl: string;
  };
  body: string;
}

/**
 * A major release group (e.g. 1.48.x) with its patch releases grouped underneath.
 * In this project's versioning scheme, release groups are keyed by the first two
 * semver parts (e.g. 1.48), with patch releases varying in the third part:
 *   - "major release group" = first two parts, e.g. 1.47.x → 1.48.x
 *   - "patch release" = third number changes, e.g. 1.48.0 → 1.48.1
 */
export interface MajorReleaseGroup {
  /** Major version key, e.g. "1.48" (first two parts) */
  majorVersion: string;
  /** Display label, e.g. "1.48.x" */
  label: string;
  /** The most recent release in this major group (used as the "hero" content) */
  latestRelease: WhatsNewRelease;
  /** All releases in this major group, sorted newest first */
  releases: WhatsNewRelease[];
  /** Date of the earliest release in this group */
  firstReleasedAt: Date;
  /** Date of the latest release in this group */
  lastReleasedAt: Date;
}

/** Maximum number of major release groups to display */
export const MAX_MAJOR_GROUPS = 9;

/**
 * Strip prerelease and build metadata from a version string.
 * e.g. "1.48.0-rc.1+build.123" → "1.48.0"
 */
function stripPrerelease(version: string): string {
  return version.replace(/^v/, '').replace(/[-+].*$/, '');
}

// Compare versions to determine if there are new releases.
// Prerelease/build metadata (e.g. -rc.1, +build) is stripped before comparison.
export function compareVersions(v1: string, v2: string): number {
  const parts1 = stripPrerelease(v1).split('.').map(Number);
  const parts2 = stripPrerelease(v2).split('.').map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 > p2) return 1;
    if (p1 < p2) return -1;
  }
  return 0;
}

/**
 * Extract the major version key from a version string.
 * e.g. "1.48.2" → "1.48" (first two semver parts).
 * This groups releases like 1.48.0, 1.48.1, 1.48.2 together.
 */
export function getMajorVersion(version: string): string {
  const parts = stripPrerelease(version).split('.');
  return `${parts[0] || '0'}.${parts[1] || '0'}`;
}

/**
 * Check if a release is a "major" release in our scheme,
 * meaning the patch (third) part is 0 — e.g. 1.48.0.
 */
export function isMajorRelease(version: string): boolean {
  const parts = stripPrerelease(version).split('.').map(Number);
  return (parts[2] ?? 0) === 0;
}

/**
 * Check if the new version is a major version bump (the major-version group changed).
 * e.g. 1.47.3 → 1.48.0 is a major bump; 1.48.0 → 1.48.1 is not.
 */
export function isMajorVersionBump(
  newVersion: string,
  oldVersion: string,
): boolean {
  const newMajor = getMajorVersion(newVersion);
  const oldMajor = getMajorVersion(oldVersion);
  return newMajor !== oldMajor && compareVersions(newVersion, oldVersion) > 0;
}

/**
 * Group a flat list of releases into major release groups.
 * Returns at most MAX_MAJOR_GROUPS groups, sorted by major version descending.
 */
export function groupReleasesByMajor(
  releases: WhatsNewRelease[],
): MajorReleaseGroup[] {
  const grouped = new Map<string, WhatsNewRelease[]>();

  for (const release of releases) {
    const major = getMajorVersion(release.version);
    if (!grouped.has(major)) {
      grouped.set(major, []);
    }
    grouped.get(major)!.push(release);
  }

  const groups: MajorReleaseGroup[] = [];

  for (const [majorVersion, groupReleases] of grouped.entries()) {
    // Sort releases within group: newest first
    const sorted = [...groupReleases].sort((a, b) =>
      compareVersions(b.version, a.version),
    );

    groups.push({
      majorVersion,
      label: `${majorVersion}.x`,
      latestRelease: sorted[0],
      releases: sorted,
      firstReleasedAt: sorted[sorted.length - 1].publishedAt,
      lastReleasedAt: sorted[0].publishedAt,
    });
  }

  // Sort groups by version descending (compare as semver, not as number)
  groups.sort((a, b) =>
    compareVersions(`${b.majorVersion}.0`, `${a.majorVersion}.0`),
  );

  // Limit to MAX_MAJOR_GROUPS
  return groups.slice(0, MAX_MAJOR_GROUPS);
}
